<template>
  <div
    v-cloak
    id="app"
    :class="{ 'mobile-view': isMobile }"
  >
    <Layout style="min-height: 100%">
      <div
        v-if="authenticated"
        v-show="hasHeader && notice.visible"
        ref="notice"
        class="notice"
      >
        <p v-dompurify-html="notice.description" />
        <span class="close">
          <i
            class="fas fa-times-circle"
            @click="closeNotice"
          />
        </span>
      </div>
      <Header
        v-if="authenticated"
        v-show="hasHeader"
        ref="header"
        class="data-portal-header"
        :style="{ top: `${noticeHeight}px` }"
      >
        <span
          ref="ruler"
          class="ruler"
        />
        <Menu
          mode="horizontal"
          :active-name="$route.path"
        >
          <div id="logo-bar">
            <MenuItem name="home">
              <router-link
                :to="{
                  name: 'Metrics Summary'
                }"
                class="layout-logo"
              >
                <img src="./assets/Lime_HorizontalLockup_White.png">
              </router-link>
            </MenuItem>
          </div>
          <template v-if="!isMobile">
            <div id="nav-bar">
              <MenuItem
                v-for="(section, index) in config"
                :key="index"
                :name="section.url"
              >
                <router-link
                  v-if="section.title !== 'Dashboards'"
                  :to="{
                    name: section.title,
                    params: { data: section }
                  }"
                >
                  <i :class="`${section.icon} menu-icon`" />
                  {{ section.title }}
                </router-link>
                <Dropdown
                  v-if="section.title === 'Dashboards'"
                  placement="bottom-start"
                >
                  <router-link
                    :to="{
                      name: section.title,
                      params: { data: section }
                    }"
                  >
                    <i :class="`${section.icon} menu-icon`" />
                    {{ section.title }}
                    <Icon type="ios-arrow-down" />
                  </router-link>
                  <DropdownMenu
                    slot="list"
                  >
                    <template v-for="category in dashboardMenu">
                      <Dropdown
                        v-if="category.items"
                        :key="category.name"
                        placement="right-start"
                      >
                        <DropdownItem>
                          <div style="display: flex; justify-content: space-between">
                            <div>{{ category.name }}</div>
                            <Icon
                              type="ios-arrow-forward"
                              style="margin: 2px 0px 0px 8px"
                            />
                          </div>
                        </DropdownItem>
                        <DropdownMenu
                          slot="list"
                          :style="{ width: `${category.width}px` }"
                        >
                          <template v-for="subCategory in category.items">
                            <Dropdown
                              v-if="subCategory.items"
                              :key="subCategory.name"
                              placement="right-start"
                            >
                              <DropdownItem>
                                {{ subCategory.name }}
                                <Icon
                                  type="ios-arrow-forward"
                                  :style="{ float: 'right', paddingTop: '2px' }"
                                />
                              </DropdownItem>
                              <DropdownMenu
                                slot="list"
                                :style="{ width: `${subCategory.width}px` }"
                              >
                                <DropdownItem
                                  v-for="subCategoryItem in subCategory.items"
                                  :key="subCategoryItem.name"
                                >
                                  <div @click="redirectToDashboard(subCategoryItem.id, subCategoryItem.name)">
                                    {{ subCategoryItem.name }}
                                  </div>
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                            <DropdownItem
                              v-else-if="subCategory.filterKey"
                              :key="subCategory.name"
                            >
                              <span @click="setExtraParam('dashboards', { 'searchValue': subCategory.filterKey })">
                                {{ subCategory.name }} {{ subCategory.filterKey }}
                              </span>
                            </DropdownItem>
                            <DropdownItem
                              v-else
                              :key="subCategory.name"
                            >
                              <div @click="redirectToDashboard(subCategory.id, subCategory.name)">
                                {{ subCategory.name }}
                              </div>
                            </DropdownItem>
                          </template>
                        </DropdownMenu>
                      </Dropdown>
                    </template>
                  </DropdownMenu>
                </Dropdown>
              </MenuItem>
              <MenuItem
                name="Help"
              >
                <a
                  :href="helpPageLink"
                  target="_blank"
                >
                  <i class="fas fa-question-circle menu-icon" />
                  Help
                </a>
              </MenuItem>
              <MenuItem
                name="Forum"
              >
                <a
                  :href="forumHomeLink"
                  target="_blank"
                >
                  <i class="fas fa-users" />
                  Forum
                </a>
              </MenuItem>
            </div>
            <div id="layout-nav">
              <MenuItem name="feedback">
                <Dropdown>
                  <a
                    href="javascript:void(0)"
                    :style="{ color: 'var(--color-white)', fontWeight: 'bold' }"
                  >
                    <i class="far fa-comments menu-icon" />
                    Feedback
                  </a>
                  <DropdownMenu slot="list">
                    <a
                      :href="contactUsOnForumLink"
                      target="_blank"
                    >
                      <DropdownItem>
                        <i class="far fa-comment action-icon" /> Contact us on Forum
                      </DropdownItem>
                    </a>
                    <a
                      href="https://lime.slack.com/app_redirect?channel=data-eng"
                      target="_blank"
                    >
                      <DropdownItem>
                        <i class="fab fa-slack action-icon" /> Find us on Slack
                      </DropdownItem>
                    </a>
                  </DropdownMenu>
                </Dropdown>
              </MenuItem>
              <MenuItem name="profile">
                <Dropdown @on-click="onUserProfileDropdownClick">
                  <a href="javascript:void(0)">
                    <Badge :style="{ color: 'var(--color-white)' }">
                      <Avatar :src="user.imageUrl" /> {{ user.name }}
                    </Badge>
                    <Icon type="ios-arrow-down" />
                  </a>
                  <DropdownMenu slot="list">
                    <DropdownItem
                      v-if="adminConsoleUsers.includes(user.email)"
                      name="adminConsole"
                    >
                      <router-link
                        :to="{
                          name: 'Admin Console'
                        }"
                        style="color: var(--color-black)"
                      >
                        <i class="fas fa-user-tie action-icon" /> Admin Console
                      </router-link>
                    </DropdownItem>
                    <DropdownItem name="logout">
                      <i class="fas fa-sign-out-alt action-icon" /> Logout
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </MenuItem>
            </div>
          </template>
          <div
            v-else
            id="layout-nav"
          >
            <Dropdown trigger="click">
              <a
                href="javascript:void(0)"
                @click="toggleDropdown"
              >
                <Icon
                  id="arrow-menu"
                  type="ios-arrow-down"
                  size="24"
                  :class="{ 'dropdown-arrow': true, 'rotate-up': dropdownVisible }"
                />
              </a>
              <DropdownMenu slot="list">
                <router-link
                  v-for="(section, index) in config"
                  :key="index"
                  :to="section.url"
                >
                  <DropdownItem v-if="section.mobile">
                    <b @click="closeMenu()">{{ section.title }}</b>
                  </DropdownItem>
                </router-link>
                <a
                  :href="helpPageLink"
                  target="_blank"
                >
                  <DropdownItem>
                    <b @click="closeMenu()">Help</b>
                  </DropdownItem>
                </a>
                <a href="mailto:data-eng-team@li.me;ruben.kogel@li.me;reza.faturechi@li.me?subject=DataPortal%20Feedback&body=Hi%20Data%20Team">
                  <DropdownItem>
                    <b @click="closeMenu()">Email Us</b>
                  </DropdownItem>
                </a>
                <a
                  href="https://lime.slack.com/app_redirect?channel=data-eng"
                  target="_blank"
                >
                  <DropdownItem>
                    <b @click="closeMenu()">Slack Us</b>
                  </DropdownItem>
                </a>
                <DropdownItem>
                  <span @click="logout()">
                    <b>Logout</b>
                  </span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </Menu>
      </Header>
      <Content
        class="data-portal-content"
        :style="{ top: `${noticeHeight}px` }"
      >
        <router-view
          :user-id="userId"
          :content-top="contentTop"
          :has-notice="hasNotice"
          :layout-heights="layoutHeights"
        />
      </Content>
      <Footer
        ref="footer"
        class="data-portal-footer"
        :style="{ marginTop: `${noticeHeight}px` }"
      >
        2020 &copy; Lime. All Rights Reserved.
      </Footer>
    </Layout>
  </div>
</template>

<script>
import isMobile from '@/helpers/isMobile'
import UserMixin from '@/mixins/UserMixin'
import axios from 'axios'

export default {
  name: 'App',

  mixins: [
    UserMixin
  ],

  data: function () {
    return {
      config: null,
      authenticated: false,
      dashboardMenu: [],
      isMobile: isMobile(),
      dropdownVisible: false,
      userId: null,
      user: {
        name: null,
        email: null,
        id: null,
        imageUrl: null
      },
      context: 'data_portal',
      refreshIntervalId: null,
      dashboardMenuWidth: 0,
      notice: {
        visible: false,
        cancelButton: ''
      },
      noticeHeight: 0,
      headerHeight: 0,
      footerHeight: 0,
      hasNotice: false
    }
  },

  computed: {
    contentTop () {
      return this.headerHeight + this.noticeHeight
    },

    layoutHeights () {
      return {
        notice: this.noticeHeight,
        header: this.headerHeight,
        footer: this.footerHeight
      }
    },

    helpPageLink () {
      return `${process.env.VUE_APP_HELP_PAGE_URL}/index.html`
    },

    forumHomeLink () {
      return process.env.VUE_APP_APP_URI + '/forum'
    },

    hasHeader () {
      return !this.$route.path.startsWith('/forum')
    },

    contactUsOnForumLink () {
      return `${process.env.VUE_APP_APP_URI}/forum?newpost=true&fullscreen=true&title=Data%20Portal&receiverEmail=data-eng-team%40li.me&feedback=true`
    }
  },

  watch: {
    // Everytime the route path changes, check for auth status
    $route () {
      this.isAuthenticated()
    },

    '$root.preferences': {
      handler: function (preferences) {
        localStorage.setItem('preferences', JSON.stringify(preferences))
      },
      deep: true
    }
  },

  async created () {
    try {
      await this.isAuthenticated()
    } catch (error) {
      console.error(error)
      this.$Message.error(`Could not get alerts ${error}`)
    }
  },

  mounted () {
    const { path } = this.$route
    const pathArr = path.split('/')

    if (pathArr.length > 1 && pathArr[1] === 'help') {
      window.location = this.helpPageLink
    }

    window.addEventListener('resize', this.checkMobile)
    this.headerHeight = this.authenticated ? this.$refs.header.$el.offsetHeight : 0
    this.footerHeight = this.$refs.footer.$el.offsetHeight
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.checkMobile)
  },

  methods: {
    async isAuthenticated () {
      try {
        this.authenticated = this.$auth.isAuthenticated()
        if (this.authenticated && this.$route) {
          if (!this.userId) {
            this.userId = await this.getUserId()
          }
          this.$auth.setId(this.userId)
          this.validateUserForPath()
          this.loadMenuConfig()
          this.fetchAdminConsoleUsers()
          this.fetchLoginNotice()
          this.$log('menu_open', this.$route.name, this.$route.name ? null : { path: this.$route.path }, this.context)
        } else {
          this.userId = null
          this.$auth.setId(null)
        }
      } catch (err) {
        await this.logOutServer()
        await this.$logout()
        console.error('User is not authenticated: ', err)
        this.$router.push('/login')
      }
    },

    async getUserId () {
      try {
        const userCallback = (result) => {
          if (result.data.hasOwnProperty('error')) {
            throw new Error(result.data.error)
          }
          if (result.data.length === 0) {
            return null
          }
          return result.data[0].id
        }
        this.user = this.$auth.getUser()
        let userId = await axios.get(process.env.VUE_APP_API_URI + '/users/user', {
          params: {
            email: this.user.email
          }
        })
          .then(userCallback)

        if (!userId) {
          userId = await axios({
            method: 'post',
            url: process.env.VUE_APP_API_URI + '/users/user',
            headers: {
              'Content-Type': 'application/json'
            },
            data: {
              'email': this.user.email,
              'name': this.user.name
            }
          })
            .then(userCallback)

          this.$log('user_create', userId, null, this.context)
        }
        return userId
      } catch (error) {
        console.error(`Can not get user ID: ${error}`)
        return null
      }
    },

    /**
     * Load menu config from server
     */
    async loadMenuConfig () {
      try {
        const config = await axios.get(`${process.env.VUE_APP_API_URI}/configs`)
        this.config = config.data
        this.loadDashboardMenu()
      } catch (error) {
        console.error(`Fail to load Config: ${error}`)
      }
    },

    async loadDashboardMenu () {
      try {
        const data = (await axios.get(`${process.env.VUE_APP_API_URI}/dashboards/menu`)).data
        this.dashboardMenu = data
      } catch (error) {
        console.error(`Fail to get dashboard menu: ${error}`)
      }
      if (!this.isMobile) {
        this.setDashboardMenuWidth()
      }
    },

    /**
     * Log out and redirect to root route
     */
    async logout () {
      await this.$log('user_logout', this.$route.name, this.$route.name ? null : { path: this.$route.path }, this.context)
      this.$auth.logout()
      await this.isAuthenticated()
      await this.logOutServer()
      this.$router.push('/login')
    },

    async logOutServer () {
      axios({
        method: 'post',
        url: process.env.VUE_APP_API_URI + '/auth/logout'
      }).catch((err) => {
        console.error('Could not log out: ', err)
      })
    },

    /**
     * Check screen width to determine if user is using mobile
     * @param {Object} e Event fired
     */
    checkMobile (e) {
      this.isMobile = isMobile(e)
    },

    /**
     * Toggle dropdown menu in mobile view
     */
    toggleDropdown () {
      this.dropdownVisible = !this.dropdownVisible
    },

    /**
     * Close dropdown menu in mobile view
     */
    closeMenu () {
      this.toggleDropdown()
    },

    /**
     * Redirect to dashboard based on ID with log
     * @param {Number} id dashboard id
     * @param {String} name dashboard name
     */
    redirectToDashboard (id) {
      this.$router.push(`/dashboards/${id}`)
    },

    /**
     * Calculate dashboard menu width
     */
    setDashboardMenuWidth () {
      this.dashboardMenu.forEach((category) => {
        let categoryWidth = this.measureMenuWidth(category.name) + 55
        if (categoryWidth > this.dashboardMenuWidth) {
          this.dashboardMenuWidth = categoryWidth
        }
        category.width = 0
        category.items.forEach((subcategory) => {
          subcategory.width = 0
          let subCategoryWidth = this.measureMenuWidth(subcategory.name) + 55
          if (subCategoryWidth > category.width) {
            category.width = subCategoryWidth
          }

          if (subcategory.items) {
            subcategory.items.forEach((dashboard) => {
              let dashboardWidth = this.measureMenuWidth(dashboard.name) + 55
              if (dashboardWidth > subcategory.width) {
                subcategory.width = dashboardWidth
              }
            })
          }
        })
      })
    },

    /**
     * Returns the width in the number of pixels.
     *
     * It gets the value by placing the input into a span element and then
     * calculating the width offset.
     *
     * @param {string} word The value to be measured.
     */
    measureMenuWidth (word) {
      const ruler = this.$refs.ruler
      ruler.textContent = word
      return ruler.offsetWidth
    },

    /**
     * Handle click on user profile dropdown
     * @param {string} name Name defined in dropdown
     */
    onUserProfileDropdownClick (name) {
      switch (name) {
        case 'logout': this.logout()
      }
    },

    /**
       * Displays a pop up to show sheet export error
       * @param {String} error represents error message to display.
       */
    onSheetCreationError (error) {
      this.$Message.error({
        content: `Error login via Google because ${error}`,
        duration: 8
      })
    },

    /**
     * Defines an extra param (prop) to send to the components.
     * NOTE: This function will be used later
     * @param {String} name
     * @param {Object} content
     */
    setExtraParam (name, content) {
      this.$router.push({
        path: '/dashboards',
        params: {
          name: content
        }
      })
    },

    /**
     * Fetches an existing notice to display.
     */
    async fetchLoginNotice () {
      if (this.notice.description) {
        return
      }

      const activeNotice = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/login-notices?isActive=true`)).data

      if (activeNotice) {
        this.$set(this.notice, 'description', activeNotice.description)
        this.$set(this.notice, 'visible', true)
        this.hasNotice = true
        this.$nextTick(() => {
          this.noticeHeight = this.$refs.notice.clientHeight
        })
      }
    },

    /**
     * Closes the action window (modal).
     */
    closeNotice () {
      this.$set(this.notice, 'visible', false)
      this.noticeHeight = 0
    },

    /**
     * Validates if the user is allowed to access to the current path.
     */
    async validateUserForPath () {
      const user = this.$auth.getUser().email.toLowerCase()
      const {
        name: sectionName,
        params: routeParams
      } = this.$route
      const paramKey = (Object.keys(routeParams))[0]

      try {
        const restrictedModule = (await axios.get(`${process.env.VUE_APP_API_URI}/admin-console/restricted-module`, {
          params: {
            sectionName,
            param: routeParams[paramKey]
          }
        })).data

        if (restrictedModule && !restrictedModule.allowedUsers.includes(user)) {
          this.$router.push('/home')
        }
      } catch (err) {
        console.error(`Could not validate user ${user} for path ${this.$route.path}`, err)
      }
    }
  }
}
</script>

<style scoped>
  #app {
    height: 100%;
  }

  .notice {
    margin: 0;
    padding: 0 8px;
    width: 100%;
    font-size: 14px;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    align-items: center;
    color: var(--color-white);
    background-color: var(--color-info);
    position: fixed;
    z-index: 1000;
  }

  .notice p {
    margin: 0;
    width: 100%;
  }

  .notice p >>> a {
    color: var(--color-yellow-light);
    text-decoration: underline;
  }

  .notice .close {
    width: 24px;
  }

  .notice .close i {
    cursor: pointer;
    margin-left: 8px;
    width: 16px;
  }

  .data-portal-header {
    display: flex;
    flex-direction: column;
    position: fixed;
    width: 100%;
    background: var(--color-green);
    z-index: 1000;
    overflow: hidden;
    padding: 0 48px;
    height: 48px;
    line-height: 48px;
  }

  .data-portal-header >>> .ivu-menu {
    background: var(--color-green);
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 48px;
    line-height: 48px;
  }

  .data-portal-header >>> .ivu-menu-horizontal.ivu-menu-light:after {
    height: 0;
    background: var(--color-green);
  }

  .data-portal-footer {
    text-align: center;
  }

  #nav-bar {
    color: var(--color-white);
    font-size: 1.3em;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    position: fixed;
  }

  #logo-bar {
    margin-right: 20px;
  }

  #nav-bar >>> .ivu-menu-item {
    height: 32px;
    line-height: 24px;
    margin-right: 30px;
    padding: 0;
    outline-color: var(--color-green-lime);
    cursor: default;
  }

  #logo-bar >>> .ivu-menu-item {
    line-height: 40px;
    height: 40px;
    padding: 0;
    outline-color: var(--color-green-lime);
    cursor: default;
  }

  #nav-bar >>> .ivu-menu-item a,
  #logo-bar >>> .ivu-menu-item a {
    color: var(--color-white);
    border-bottom: none;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
  }

  #nav-bar >>> .ivu-menu-item a {
    line-height: 32px;
    height: 32px;
  }

  #logo-bar >>> .ivu-menu-item a {
    line-height: 40px;
    height: 40px;
    width: 100px;
  }

  #nav-bar >>> .ivu-menu-item-active,
  #logo-bar >>> .ivu-menu-item-active,
  #nav-bar >>> .ivu-menu-item a.ivu-menu-item-active,
  #logo-bar >>> .ivu-menu-item a.ivu-menu-item-active {
    border-bottom: 2px solid var(--color-green-hover);
  }

  #nav-bar >>> .ivu-menu-item a:hover,
  #logo-bar >>> .ivu-menu-item a:hover {
    color: var(--color-white);
  }

  #nav-bar >>> .ivu-menu-item:hover,
  #logo-bar >>> .ivu-menu-item:hover {
    border-color: var(--color-green-lime);
  }

  #nav-bar >>> .ivu-icon,
  #logo-bar >>> .ivu-icon {
    font-weight: 700;
  }

  #nav-bar >>> .ivu-dropdown-item a {
    color: var(--color-black);
    line-height: normal;
    height: 16px;
  }

  #nav-bar >>> .ivu-dropdown-item a:hover {
    color: var(--color-black);
  }

  #nav-bar >>> .ivu-dropdown-item a.ivu-menu-item-active {
    border-bottom: unset;
  }

  #nav-bar >>> .ivu-dropdown .ivu-select-dropdown {
    display: inline;
    width: auto;
  }

  #layout-nav {
    margin: 0 auto;
    margin-right: 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  #layout-nav >>> .ivu-dropdown-rel .ivu-icon {
    color: var(--color-white);
  }

  #layout-nav >>> .ivu-icon {
    font-weight: 700;
  }

  #layout-nav >>> .ivu-menu-item:hover {
    border: none;
  }

  #layout-nav >>> .ivu-menu-item-active,
  #layout-nav >>> .ivu-menu-item a.ivu-menu-item-active {
    border-bottom: none;
  }

  #layout-nav >>> .ivu-menu-item-active {
    color: var(--color-black);
  }

  .nav-tool-tip.ivu-tooltip >>> .ivu-tooltip-popper {
    top: 40px !important;
  }

  .layout {
    border: 1px solid var(--color-gray-border);
    background: var(--color-body-background);
    position: relative;
    border-radius: 4px;
    overflow: hidden;
  }

  .layout-logo {
    width: calc(100% - 48px);
    height: 40px;
    border-radius: 3px;
    position: relative;
    padding-left: 24px;
    display: flex;
    align-items: center;
  }

  .layout-logo img {
    height: 64px;
    margin-left: -16px;
  }

  .layout-menu-button-container {
    position: relative;
    float: left;
  }

  .menu-button{
    background-color: var(--color-green-lime);
    border-color: var(--color-white);
  }
  .menu-button i {
    color: var(--color-white);
  }
  .menu-button:hover i {
    color: var(--color-info);
  }
  .data-portal-menu {
    position: fixed;
  }
  .data-portal-menu li {
    font-size: 24px;
    color: var(--color-white);
  }
  .data-portal-menu span {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
    transition: width .2s ease .2s;
  }
  .collapsed-menu span {
    display: none;
  }
  .collapsed-menu i {
    font-size: 32px;
  }
  .data-portal-content {
    position: relative;
    margin: 48px 48px 0;
    min-height: 500px;
  }
  .mobile-view .data-portal-content {
    margin: 48px 16px 0;
  }
  .mobile-view .splash-page {
    padding-top: 40%;
    padding-left: 10%;
  }
  .mobile-view .data-portal-header {
    padding: 0;
  }
  .mobile-view #logo-bar {
    margin-left: 20px;
  }

  .mobile-view .layout-logo img {
    margin-left: -38px;
  }

  .dropdown-arrow {
    transform: rotate(0deg);
    transition: transform 0.5s linear;
  }
  .dropdown-arrow.rotate-up {
    transform: rotate(180deg);
    transition: transform 0.5s linear;
  }

  .menu-icon,
  .action-icon {
    margin-right: 4px;
    display: inline-block;
    font-size: 12px;
    line-height: normal;
    width: 15px;
    text-align: center;
    vertical-align: center;
  }

  .menu-icon {
    font-size: 18px;
    width: 18px;
  }

  .ruler {
    font-size: 12px;
    position: absolute;
    visibility: hidden;
    white-space: nowrap;
  }

  .modal-title {
    text-align: center;
  }

  @media screen and (min-width: 1024px) {
    .layout-logo {
      width: 120px;
      padding-left: 0;
      padding-right: 8px;
    }

    #nav-bar {
      margin-left: 120px;
      font-size: 1em;
      width: calc(100% - 120px - 104px);
      height: 40px;
      align-items: center;
    }

    #nav-bar >>> .ivu-menu-item {
      margin-right: 16px;
    }

    #layout-nav {
      justify-content: flex-end;
      margin-right: 0;
      position: fixed;
      right: 48px;
    }

    #layout-nav >>> .ivu-menu-item {
      padding-left: 8px;
      padding-right: 0;
      border: none;
    }

    #nav-bar >>> .ivu-menu-item a {
      font-size: 14px;
    }

    #nav-bar >>> .ivu-dropdown-item a {
      font-size: 12px;
    }
  }

  @media screen and (max-width: 1023px) {
    #layout-nav >>> .ivu-select-dropdown {
      position: fixed !important;
    }
  }

  @media screen and (min-width: 1024px) {
    .mobile-view .data-portal-content {
      position: relative;
      margin: 48px 48px 0;
      min-height: 500px;
    }
  }

  @media screen and (min-width: 1440px) {
    #nav-bar >>> .ivu-menu-item {
      margin-right: 24px;
    }
  }
</style>
